import React, { useState, memo } from "react";
import { ChevronDown } from "lucide-react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import ReCAPTCHA from "react-google-recaptcha";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const FormInput = memo(
  ({
    id,
    label,
    type = "text",
    required = false,
    placeholder = "",
    value,
    onChange,
  }) => (
    <div className="relative w-full">
      <input
        type={type}
        id={id}
        name={id}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="peer block w-full appearance-none border-b border-gray-300 bg-transparent px-2.5 pt-4 pb-2 text-sm text-gray-900 focus:border-gray-500 focus:outline-none focus:ring-0"
      />
      <label
        htmlFor={id}
        className={`absolute top-4 left-2.5 z-1 origin-[0] -translate-y-4 scale-75 transform cursor-text select-none text-sm text-gray-500 duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:text-gray-500 ${
          required ? "after:content-['*'] after:text-red-500 after:ml-0.5" : ""
        }`}
      >
        {label}
      </label>
    </div>
  ),
);

const FormSelect = memo(
  ({ id, label, required = false, options = [], value, onChange }) => (
    <div className="relative w-full">
      <select
        id={id}
        name={id}
        value={value}
        onChange={onChange}
        required={required}
        className="peer block w-full appearance-none  border-b border-gray-300 bg-transparent px-2.5 pt-4 pb-2 text-sm text-gray-900 focus:border-gray-500 focus:outline-none focus:ring-0"
      >
        <option value="" disabled hidden></option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <label
        htmlFor={id}
        className={`absolute top-4 left-2.5 z-1 origin-[0] -translate-y-4 scale-75 transform cursor-text select-none text-lg text-gray-500 duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:text-gray-500 ${
          required ? "after:content-['*'] after:text-red-500 after:ml-0.5" : ""
        }`}
      >
        {label}
      </label>
      <ChevronDown className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400 pointer-events-none" />
    </div>
  ),
);

const ListwithusForm = ({ isCaptcha, type = "footer" }) => {
  const [captchaToken, setCaptchaToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    propertyType: "",
    bedrooms: "",
    buildingName: "",
    message: "",
    furnishingStatus: "",
    token: "",
  });

  const handleCaptcha = (value) => {
    setCaptchaToken(value);
    setFormData((prev) => ({ ...prev, token: value }));
  };

  const [formErrors, setFormErrors] = useState({});
  const [submissionMessage, setSubmissionMessage] = useState("");

  const validateForm = () => {
    const errors = {};
    if (!formData.firstName) errors.firstName = "First Name is required.";
    if (!formData.lastName) errors.lastName = "Last Name is required.";
    if (!formData.email) errors.email = "Email Address is required.";
    if (!formData.phoneNumber) errors.phoneNumber = "Phone Number is required.";
    if (!formData.propertyType)
      errors.propertyType = "Property Type is required.";
    if (!formData.bedrooms) errors.bedrooms = "Number of Bedrooms is required.";
    if (!formData.buildingName)
      errors.buildingName = "Building Name is required.";
    if (!formData.furnishingStatus)
      errors.furnishingStatus = "Furnishing Status is required.";
    return errors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handlePhoneChange = (phone) => {
    setFormData((prev) => ({ ...prev, phoneNumber: phone }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!captchaToken) {
      alert("Please verify that you're not a robot.");
      return;
    }

    const errors = validateForm();
    if (Object.keys(errors).length === 0) {
      try {
        await axios.post("https://365luxuryhomes.com/api/list", formData);

        setSubmissionMessage("Form submitted successfully!");
        setFormErrors({});
        navigate("/thank-you");
        setLoading(false);
      } catch (error) {
        console.error("Error submitting form:", error);
        alert("There was an error submitting the form. Please try again.");
        setLoading(false);
      }
    } else {
      setFormErrors(errors);
      setSubmissionMessage("Please fill out all required fields.");
    }
  };

  return (
    <div className="w-full mx-auto max-w-4xl rounded-3xl bg-[#efeeed] p-8 shadow-xl md:p-12">
      <h1 className="mb-8 text-2xl font-normal text-gray-800 md:text-3xl">
        {type === "footer"
          ? " Property Evaluation"
          : " List Your Property with Us"}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormInput
            id="firstName"
            label="First Name"
            required={true}
            value={formData.firstName}
            onChange={handleChange}
          />
          <FormInput
            id="lastName"
            label="Last Name"
            required={true}
            value={formData.lastName}
            onChange={handleChange}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormInput
            id="email"
            label="Email Address"
            type="email"
            required={true}
            value={formData.email}
            onChange={handleChange}
          />
          <div className="relative w-full">
            <div className="flex items-center  has-[input:focus]:border-blue-600">
              <PhoneInput
                country={"ae"}
                value={formData.phoneNumber}
                onChange={handlePhoneChange}
                className="px-2.5 pt-[8px] "
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormSelect
            id="propertyType"
            label="Property Type"
            required={true}
            options={["Apartment", "Villa", "Townhouse"]}
            value={formData.propertyType}
            onChange={handleChange}
          />
          <FormSelect
            id="bedrooms"
            label="No of Bedrooms"
            required={true}
            options={["1", "2", "3", "4", "5+"]}
            value={formData.bedrooms}
            onChange={handleChange}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormInput
            id="buildingName"
            label="Building Name"
            required={true}
            value={formData.buildingName}
            onChange={handleChange}
          />
          <FormSelect
            id="furnishingStatus"
            label="Furnishing Status"
            required={true}
            options={["Furnished", "Unfurnished", "Partially Furnished"]}
            value={formData.furnishingStatus}
            onChange={handleChange}
          />
        </div>

        <div>
          <FormInput
            id="message"
            label="Message"
            value={formData.message}
            onChange={handleChange}
          />
        </div>

        {submissionMessage && (
          <div
            className={`p-4 text-center ${
              Object.keys(formErrors).length === 0
                ? "bg-green-100 text-green-700"
                : "bg-red-100 text-red-700"
            }`}
          >
            {submissionMessage}
          </div>
        )}

        <div className="flex md:flex-row flex-col gap-4 items-center justify-between">
          <div className="relative z-[999]">
            <ReCAPTCHA
              sitekey="6LfeklorAAAAAPUFX08mcywgvxkSSOv-dczoUTrv"
              onChange={handleCaptcha}
              className="!rounded-[8px]"
            />
          </div>
          <button
            type="submit"
            className="w-full transform bg-white py-1 rounded-md text-lg text-[#b6b6b6] shadow-md transition-transform duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-blue-300 md:w-auto md:px-12"
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ListwithusForm;
